import { Button, ButtonProps, CircularProgress } from "@mui/material";
import React from "react";

interface AuthButtonProps extends Omit<ButtonProps, "variant"> {
  loading?: boolean;
  variant?: "primary" | "secondary" | "social";
}

const primarySx = {
  borderRadius: "999px",
  py: 1.5,
  px: 2,
  textTransform: "none" as const,
  fontSize: 18,
  fontFamily: "var(--font-display), Rubik, Tajawal, sans-serif",
  fontWeight: 800,
  letterSpacing: "-0.01em",
  background: "var(--hot)",
  color: "#fff",
  boxShadow:
    "0 1px 0 rgba(255,255,255,0.35) inset, 0 8px 0 color-mix(in oklab, var(--hot) 50%, #000), 0 14px 30px -10px color-mix(in oklab, var(--hot) 70%, transparent)",
  "&:hover": {
    background: "color-mix(in oklab, var(--hot) 92%, #000)",
    boxShadow:
      "0 1px 0 rgba(255,255,255,0.35) inset, 0 6px 0 color-mix(in oklab, var(--hot) 50%, #000)",
  },
  "&:active": {
    transform: "translateY(2px) scale(0.99)",
  },
  "&.Mui-disabled": {
    opacity: 0.55,
    color: "#fff",
  },
};

const secondarySx = {
  borderRadius: "999px",
  py: 1.5,
  textTransform: "none" as const,
  fontWeight: 700,
  fontFamily: "var(--font-display), Rubik, sans-serif",
  background: "linear-gradient(135deg, var(--violet) 0%, color-mix(in oklab, var(--violet) 75%, #000) 100%)",
  color: "#fff",
  boxShadow: "0 4px 16px color-mix(in oklab, var(--violet) 40%, transparent)",
  "&:hover": {
    filter: "brightness(1.06)",
  },
};

const AuthButton: React.FC<AuthButtonProps> = ({
  loading = false,
  variant = "primary",
  children,
  sx,
  disabled,
  ...props
}) => {
  const variantSx =
    variant === "primary"
      ? primarySx
      : variant === "secondary" || variant === "social"
        ? secondarySx
        : primarySx;

  return (
    <Button
      {...props}
      disabled={disabled || loading}
      variant="contained"
      size="large"
      fullWidth
      disableElevation
      sx={{
        ...variantSx,
        ...sx,
      }}
    >
      {loading ? <CircularProgress size={24} color="inherit" /> : children}
    </Button>
  );
};

export default AuthButton;
